import { ChangeEvent, useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import { useOnboardingContext } from 'context/OnboardingContext'
import mixpanel from 'mixpanel-browser'
import { ActivateAccountFormsProps } from 'templates/ActivateAccount/ActivateAccount'

import AutoCompleteBank from 'components/AutoCompleteBank'
import IconContainer from 'components/IconContainer/IconContainer'
import InputController from 'components/InputController'

import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import {
  ACCOUNT_DV_ACCEPTED_CHAR,
  ACCOUNT_DV_LENGTH_MESSAGE,
  ACCOUNT_DV_MAX_LENGTH,
  AGENCY_ACCEPTED_CHAR,
  AGENCY_LENGTH_MESSAGE,
  AGENCY_MAX_LENGTH,
  BankFormSchema,
  schemaBankForm
} from './'

export const initialBankDataFormValue = {}

const BankDataForm = ({
  sucessSubmit,
  handleBackStep,
  onboarding,
  handleCloseModal
}: ActivateAccountFormsProps & { handleCloseModal: () => void }) => {
  const { handleChange, values, fetchOnboarding } = useOnboardingContext()

  const { handleSubmit, control, setError, clearErrors } =
    useForm<BankFormSchema>({
      defaultValues: values['3'],
      resolver: yupResolver(schemaBankForm)
    })

  // Corta o caractere excedente já na digitação, mas sempre acompanhado da
  // mensagem. O corte silencioso (maxLength) inverteria o bug que este fix
  // ataca: quem digita "08" no dígito verificador quer dizer "8", e o campo
  // guardaria "0" sem ninguém perceber — DV errado quebra a transferência
  // porque o account_number é a concatenação de account + account_dv.
  const handleLimitedChange =
    (
      name: 'agency' | 'account_dv',
      maxLength: number,
      acceptedChar: RegExp,
      message: string,
      onChange: (value: string) => void
    ) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const typed = event.target.value
      const limited = typed
        .split('')
        .filter((char) => acceptedChar.test(char))
        .join('')
        .slice(0, maxLength)

      if (limited === typed) {
        clearErrors(name)
      } else {
        setError(name, { type: 'limit', message })
      }

      onChange(limited)
    }

  const onSubmit: SubmitHandler<BankFormSchema> = async (data) => {
    const { bank, ...rest } = data

    const response = await DataService({
      type: 'POST',
      data: {
        bank_account: {
          ...rest,
          bank_uuid: bank.uuid
        }
      },
      url: '/v1/bank_accounts'
    })

    const error = response?.status >= 400 ? true : false

    if (!error) {
      await DataService({
        type: 'PATCH',
        url: '/v1/onboarding/bank_account_saved'
      })

      handleChange(3, data)
    }

    // SC-10832: repassa o pedido de confirmação para quem abriu o formulário,
    // que é quem abre o modal do código.
    return sucessSubmit({
      error,
      message: response?.message,
      uuid: response?.data?.record?.uuid,
      verificationRequired: response?.data?.verification_required,
      availableChannels: response?.data?.available_channels
    })
  }

  useEffect(() => {
    if (!onboarding) {
      fetchOnboarding()
    }
  }, [])
  const mixpanelTyped = mixpanel as unknown as { __loaded: string }
  return (
    <ModalBody
      component="form"
      onSubmit={handleSubmit(onSubmit, toastFirstError)}
    >
      {!onboarding ? (
        <ModalHeader onClose={handleCloseModal}>
          <Stack direction="row" gap={2} alignItems="center">
            <IconContainer icon="accountBalance" />
            <Typography variant="mdBold">
              Informe os dados da sua conta bancária
            </Typography>
          </Stack>
        </ModalHeader>
      ) : (
        <Typography
          sx={{
            [theme.breakpoints.down(800)]: {
              fontSize: '18px'
            }
          }}
          variant="lgBold"
        >
          Informe os dados da sua conta bancária
        </Typography>
      )}

      <ModalContent>
        <Stack>
          <li>
            <Typography variant="smLight">
              CPF deve receber em uma conta bancária PF, da mesma titularidade.
            </Typography>
          </li>
          <li>
            <Typography variant="smLight">
              CNPJ deve receber em uma conta bancária PJ, da mesma titularidade.
            </Typography>
          </li>
        </Stack>

        <Box mt="1.5rem" display="flex" flexDirection="column" gap="15px">
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Textfield
                value={values[1].legal_name}
                label="Nome do titular"
                disabled
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Textfield value={values[1].document} label="CPF/CNPJ" disabled />
            </Grid>
          </Grid>

          <AutoCompleteBank control={control} name="bank" />
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <Controller
                control={control}
                name="agency"
                render={({
                  field: { ref, onChange, ...field },
                  fieldState: { error }
                }) => (
                  <Textfield
                    {...field}
                    inputRef={ref}
                    placeholder="0000"
                    label="Número da agência bancária"
                    inputProps={{ inputMode: 'numeric' }}
                    onChange={handleLimitedChange(
                      'agency',
                      AGENCY_MAX_LENGTH,
                      AGENCY_ACCEPTED_CHAR,
                      AGENCY_LENGTH_MESSAGE,
                      onChange
                    )}
                    helperText={error?.message}
                    error={!!error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <InputController control={control} name="account">
                <Textfield
                  placeholder="0000000"
                  label="Número da conta (sem o dígito)"
                  inputProps={{ inputMode: 'numeric' }}
                />
              </InputController>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Controller
                control={control}
                name="account_dv"
                render={({
                  field: { ref, onChange, ...field },
                  fieldState: { error }
                }) => (
                  <Textfield
                    {...field}
                    inputRef={ref}
                    placeholder="0"
                    label="Dígito verificador da conta"
                    onChange={handleLimitedChange(
                      'account_dv',
                      ACCOUNT_DV_MAX_LENGTH,
                      ACCOUNT_DV_ACCEPTED_CHAR,
                      ACCOUNT_DV_LENGTH_MESSAGE,
                      onChange
                    )}
                    helperText={error?.message}
                    error={!!error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </ModalContent>

      <ModalFooter>
        {/* Fora do onboarding não há etapa anterior: o mesmo botão fecha o modal. */}
        <Button
          variantButton="neutral"
          type="button"
          onClick={onboarding ? handleBackStep : handleCloseModal}
        >
          {onboarding ? 'Voltar' : 'Cancelar'}
        </Button>
        <Box
          onClick={() =>
            mixpanelTyped.__loaded &&
            mixpanel.track('Preencheu dados bancários concluir cadastro')
          }
        >
          <Button variantButton="primary" type="submit">
            Salvar
          </Button>
        </Box>
      </ModalFooter>
    </ModalBody>
  )
}

export default BankDataForm
